import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';

import type { ConnectionHealth } from '@core/core.public-api';
// Import profundo deliberado: el barrel completo de shared arrastra map/charts al bundle inicial.
import { IconComponent } from '@shared/ui/atoms/icon/icon.component';
import { SkeletonComponent } from '@shared/ui/atoms/skeleton/skeleton.component';

import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { NotificationBellComponent } from './notification-bell.component';

/**
 * Topbar (dumb / presentational respecto del dominio: recibe todo por input(), emite por
 * output(); los breadcrumbs son infra de layout auto-contenida y viven integrados aca —
 * una sola barra, sin fila separada).
 *
 * Muestra: toggle de sidenav + breadcrumbs (izquierda); chip de tenant legible (+ boton de
 * "volver a mi tenant" al impersonar), indicador realtime (dot + label — nunca color solo)
 * y menu de usuario con avatar de iniciales y logout (derecha).
 *
 * Nota: el selector de "actuar como" otro tenant vive en el feature admin (necesita el listado
 * de tenants desde la API). Aca solo reflejamos el estado y ofrecemos el reset — la autoridad
 * del tenant es el claim del JWT, el override es explicito y auditable.
 */
@Component({
    selector: 'app-topbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        IconComponent,
        SkeletonComponent,
        BreadcrumbsComponent,
        NotificationBellComponent,
    ],
    template: `
    <header class="topbar">
      <button type="button" class="topbar__burger" (click)="toggleSidenav.emit()" aria-label="Alternar menu">
        <pvp-icon name="menu" size="md" />
      </button>

      <app-breadcrumbs class="topbar__crumbs" />

      <span class="topbar__spacer"></span>

      <span
        class="topbar__tenant"
        [class.topbar__tenant--impersonating]="isImpersonating()"
        [attr.title]="tenantId()"
      >
        {{ tenantLabel() }}
      </span>
      @if (isImpersonating()) {
        <button type="button" class="topbar__reset" (click)="resetTenant.emit()">
          Volver a mi tenant
        </button>
      }

      <span
        class="topbar__conn"
        [attr.title]="connectionLabel()"
        role="status"
        [attr.aria-label]="'Conexion en tiempo real: ' + connectionLabel()"
      >
        <span
          class="topbar__conn-dot"
          [class.topbar__conn-dot--green]="connectionHealth() === 'green'"
          [class.topbar__conn-dot--yellow]="connectionHealth() === 'yellow'"
          [class.topbar__conn-dot--red]="connectionHealth() === 'red'"
          aria-hidden="true"
        ></span>
        <span class="topbar__conn-label">{{ connectionLabel() }}</span>
      </span>

      <app-notification-bell />

      <a
        class="topbar__user"
        routerLink="/account"
        [attr.title]="userTooltip()"
        aria-label="Mi cuenta"
      >
        @if (userNameLoading()) {
          <pvp-skeleton class="topbar__user-skeleton" variant="circle" [width]="28" [height]="28" />
          <span class="topbar__user-name">
            <pvp-skeleton variant="text" [width]="88" [height]="12" />
          </span>
        } @else {
          <span class="topbar__avatar" aria-hidden="true">{{ userInitials() }}</span>
          <span class="topbar__user-name">{{ userName() }}</span>
        }
      </a>

      @if (showChangePassword()) {
        <a
          class="topbar__changepw"
          routerLink="/account/change-password"
          title="Cambiar contrasena"
          aria-label="Cambiar contrasena"
        >
          <pvp-icon name="lock" size="sm" />
          <span class="topbar__changepw-label">Cambiar contrasena</span>
        </a>
      }

      <button
        type="button"
        class="topbar__logout"
        (click)="logout.emit()"
        title="Cerrar sesion"
        aria-label="Cerrar sesion"
      >
        <pvp-icon name="log-out" size="sm" />
        <span class="topbar__logout-label">Salir</span>
      </button>
    </header>
  `,
    styles: [
        `
      .topbar {
        display: flex;
        align-items: center;
        gap: 12px;
        height: 56px;
        padding: 0 16px;
        background: var(--pvp-color-surface);
        border-bottom: 1px solid var(--pvp-color-border);
        color: var(--pvp-color-text);
      }
      .topbar__burger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        cursor: pointer;
        transition:
          background-color 150ms ease,
          color 150ms ease;
      }
      .topbar__burger:hover {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .topbar__crumbs {
        min-width: 0;
        overflow: hidden;
      }
      .topbar__spacer {
        flex: 1;
      }
      .topbar__tenant {
        padding: 4px 10px;
        border-radius: 999px;
        border: 1px solid var(--pvp-color-border);
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text-muted);
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        cursor: default;
      }
      .topbar__tenant--impersonating {
        border-color: var(--pvp-color-warning);
        background: var(--pvp-color-warning-tint);
        color: var(--pvp-color-warning);
        font-weight: 600;
      }
      .topbar__reset {
        padding: 4px 10px;
        background: transparent;
        border: 1px solid var(--pvp-color-border-strong);
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        font-size: 12px;
        cursor: pointer;
        transition:
          background-color 150ms ease,
          color 150ms ease;
      }
      .topbar__reset:hover {
        background: var(--pvp-color-surface-raised);
        color: var(--pvp-color-text);
      }
      .topbar__conn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
      }
      .topbar__conn-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--pvp-color-text-subtle);
      }
      .topbar__conn-dot--green {
        background: var(--pvp-color-online);
      }
      .topbar__conn-dot--yellow {
        background: var(--pvp-color-degraded);
      }
      .topbar__conn-dot--red {
        background: var(--pvp-color-offline);
      }
      .topbar__conn-label {
        font-size: 12px;
        color: var(--pvp-color-text-muted);
      }
      .topbar__user {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        padding: 3px 6px;
        border-radius: var(--pvp-radius-sm);
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        transition: background-color 150ms ease;
      }
      .topbar__user:hover {
        background: var(--pvp-color-surface-raised);
      }
      .topbar__avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: none;
        border-radius: 50%;
        background: var(--pvp-color-primary-tint);
        border: 1px solid var(--pvp-color-border-strong);
        color: var(--pvp-color-primary);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      .topbar__user-name {
        font-size: 13px;
        color: var(--pvp-color-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
      }
      /* El skeleton del avatar ocupa el mismo hueco que el avatar real (sin encogerse). */
      .topbar__user-skeleton {
        flex: none;
      }
      .topbar__changepw,
      .topbar__logout {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: transparent;
        border: 1px solid var(--pvp-color-border);
        border-radius: var(--pvp-radius-sm);
        color: var(--pvp-color-text-muted);
        font-size: 12.5px;
        text-decoration: none;
        cursor: pointer;
        transition:
          background-color 150ms ease,
          color 150ms ease,
          border-color 150ms ease;
      }
      .topbar__changepw:hover,
      .topbar__logout:hover {
        background: var(--pvp-color-surface-raised);
        border-color: var(--pvp-color-border-strong);
        color: var(--pvp-color-text);
      }

      /* Mobile (<md): los chips no entran en 375px. Se ocultan tenant y label de
         usuario (quedan avatar + Salir solo icono); el estado de conexion conserva
         su label (dot nunca color-solo). El detalle sigue accesible via tooltip/aria. */
      @media (max-width: 767px) {
        .topbar {
          gap: 8px;
          padding: 0 12px;
        }
        .topbar__tenant {
          display: none;
        }
        .topbar__user-name {
          display: none;
        }
        .topbar__changepw-label,
        .topbar__logout-label {
          display: none;
        }
        .topbar__changepw,
        .topbar__logout {
          padding: 6px 8px;
        }
        .topbar__reset {
          font-size: 11px;
          padding: 4px 8px;
        }
      }
    `,
    ]
})
export class TopbarComponent {
  /** Nombre VISIBLE del usuario (ya humanizado por el container — nunca un UUID crudo). */
  readonly userName = input.required<string>();
  /** True mientras el perfil (`/auth/me`) esta en vuelo: skeleton en vez de nombre. */
  readonly userNameLoading = input<boolean>(false);
  /** Detalle opcional (email / id) que solo se expone en el tooltip del chip. */
  readonly userDetail = input<string>('');
  /** Nombre legible del tenant (resuelto por el container — no el UUID crudo). */
  readonly tenantLabel = input.required<string>();
  /** UUID real del tenant (tooltip / trazabilidad). */
  readonly tenantId = input<string | null>(null);
  readonly isImpersonating = input.required<boolean>();
  readonly connectionHealth = input.required<ConnectionHealth>();
  /**
   * Muestra el acceso a "Cambiar contrasena" (LION-760). Solo en modo local: los
   * usuarios SSO cambian su contrasena en el IdP (el container lo resuelve desde
   * `AuthStore.isLocalMode()`).
   */
  readonly showChangePassword = input<boolean>(false);

  readonly toggleSidenav = output<void>();
  readonly resetTenant = output<void>();
  readonly logout = output<void>();

  protected readonly connectionLabel = computed(() => {
    switch (this.connectionHealth()) {
      case 'green':
        return 'En linea';
      case 'yellow':
        return 'Reconectando';
      case 'red':
        return 'Sin conexion';
      default:
        return 'Sin iniciar';
    }
  });

  /** Tooltip del chip: nombre visible + detalle (email/id) si difiere. */
  protected readonly userTooltip = computed(() => {
    const name = this.userName();
    const detail = this.userDetail();
    // Sin nombre (perfil en vuelo o fallido) el detalle va solo: nunca " — <id>".
    if (!name) {
      return detail;
    }
    if (!detail || detail === name) {
      return name;
    }
    return `${name} — ${detail}`;
  });

  /**
   * Iniciales del nombre VISIBLE para el avatar (2 letras): primeras letras de
   * las dos primeras palabras ("Admin Pampa" → "AP"); con una sola palabra,
   * sus dos primeras letras ("HSE" → "HS").
   */
  protected readonly userInitials = computed(() => {
    const name = this.userName().trim();
    if (!name) {
      return '—';
    }
    const parts = name.split(/\s+/);
    const initials =
      parts.length >= 2
        ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`
        : (parts[0] ?? '').slice(0, 2);
    return initials.toUpperCase() || '—';
  });
}
